import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { JsonForms } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import type { ErrorObject } from 'ajv';
import { tenXService } from '../../../../../services/TenXService';
import {
  computeConfigDiff,
  blockingValidationErrors,
  schemaDefaults,
  buildUiSchema,
  buildOverwrite,
} from '../configUtil';
import type {
  ConfigParameters,
  DeviceStatus,
  JsonSchema,
} from '../configTypes';
import ConfigDiffDialog from './configDiffDialog';
import RecentDeployments from './recentDeployments';
import styles from './deviceConfiguration.module.css';

interface DeviceConfigurationProps {
  deviceName: string;
}

const statusColor = (status: DeviceStatus): 'success' | 'default' | 'warning' => {
  if (status === 'connected') return 'success';
  if (status === 'disconnected') return 'warning';
  return 'default';
};

// Diff baseline = device's running config (`reported`) + schema defaults
// 'configured' parameters may not actually be on the device
// schema defaults are device param defaults.
const buildBaseline = (
  configuration: { reported?: ConfigParameters | null },
  deviceSchema: JsonSchema
): ConfigParameters => ({
  ...schemaDefaults(deviceSchema),
  ...(configuration.reported ?? {}),
});

const fetchDevice = async (deviceName: string) => {
  const [status, version, configuration, schema] = await Promise.all([
    tenXService.getDeviceStatus(deviceName),
    tenXService.getDeviceVersion(deviceName),
    tenXService.getDeviceConfiguration(deviceName),
    tenXService.getDeviceConfigurationSchema(deviceName),
  ]);
  return { status, version, schema, config: buildBaseline(configuration, schema) };
};

const DeviceConfiguration: React.FC<DeviceConfigurationProps> = ({ deviceName }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<DeviceStatus>('unknown');
  const [version, setVersion] = useState<string>('');
  const [schema, setSchema] = useState<JsonSchema | null>(null);

  const [baseline, setBaseline] = useState<ConfigParameters>({});
  const [data, setData] = useState<ConfigParameters>({});
  const [formErrors, setFormErrors] = useState<ErrorObject[]>([]);

  const [overwrite, setOverwrite] = useState<boolean>(false);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [opening, setOpening] = useState<boolean>(false);
  const [deploying, setDeploying] = useState<boolean>(false);
  const [deployedId, setDeployedId] = useState<string | null>(null);

  const [refreshing, setRefreshing] = useState<boolean>(false);
  // Bumped on each manual refresh so RecentDeployments reloads with the page.
  const [refreshToken, setRefreshToken] = useState<number>(0);

  const applyDevice = useCallback((device: Awaited<ReturnType<typeof fetchDevice>>) => {
    setStatus(device.status);
    setVersion(device.version);
    setSchema(device.schema);
    setBaseline(device.config);
    setData(device.config);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setDeployedId(null);

      try {
        const device = await fetchDevice(deviceName);

        if (cancelled) return;

        applyDevice(device);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load device configuration';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [deviceName, applyDevice]);

  // Quiet reload: keeps the form mounted (no spinner) and replaces status,
  // firmware, schema and config, discarding unsaved edits.
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      applyDevice(await fetchDevice(deviceName));
      setRefreshToken((token) => token + 1);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to refresh device configuration';
      setError(message);
    } finally {
      setRefreshing(false);
    }
  };

  const uiSchema = useMemo(() => buildUiSchema(schema), [schema]);

  const changeDiff = computeConfigDiff(baseline, data);
  const mergePayload = Object.fromEntries(changeDiff.map((d) => [d.field, d.next]));

  // Default parameters are written by removing them from the config
  const { payload: overwritePayload, diff: overwriteDiff } = buildOverwrite(
    baseline,
    changeDiff,
    schema
  );
  const displayDiff = overwrite ? overwriteDiff : changeDiff;

  const setFields = overwrite
    ? Object.keys(overwritePayload)
    : changeDiff.map((d) => d.field);
  const blockingErrors = blockingValidationErrors(formErrors, setFields);

  const noChanges = !overwrite && changeDiff.length === 0;
  const deployDisabled = blockingErrors.length > 0 || noChanges;

  let disabledReason = '';
  if (noChanges) {
    disabledReason = 'No changes to deploy.';
  } else if (blockingErrors.length > 0) {
    disabledReason = 'Resolve the highlighted errors above before deploying.';
  }

  // Refetch `reported` on deployments to have accurate diff
  const handleOpenDeploy = async () => {
    if (!schema) return;
    setOpening(true);
    setError(null);
    try {
      const configuration = await tenXService.getDeviceConfiguration(deviceName);
      const freshBaseline = buildBaseline(configuration, schema);
      setBaseline(freshBaseline);
      // Open only if the form still differs from the just-fetched record; otherwise the recomputed diff grays the button out.
      if (overwrite || computeConfigDiff(freshBaseline, data).length > 0) {
        setDialogOpen(true);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to refresh device configuration';
      setError(message);
    } finally {
      setOpening(false);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      // Merge sends only the changed fields; overwrite sends defaults + changes.
      const payload = overwrite ? overwritePayload : mergePayload;
      const deploymentId = await tenXService.deployDeviceConfiguration(
        deviceName,
        payload,
        overwrite
      );
      setDeployedId(deploymentId);
      setDialogOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to deploy configuration';
      setError(message);
      setDialogOpen(false);
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <Box className={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  // Rendered in every state so a failed load can be retried.
  const refreshButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<RefreshIcon />}
      onClick={handleRefresh}
      disabled={refreshing || opening || deploying}
    >
      {refreshing ? 'Refreshing…' : 'Refresh'}
    </Button>
  );

  if (error) {
    return (
      <Box className={styles.section}>
        <Box className={styles.header}>{refreshButton}</Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className={styles.section}>
      {/* Header: status + firmware version + refresh */}
      <Box className={styles.header}>
        <Box className={styles.statusRow}>
          <Chip label={status} color={statusColor(status)} size="small" />
          <Typography variant="body2" color="text.secondary">
            Firmware: {version || 'unknown'}
          </Typography>
        </Box>
        {refreshButton}
      </Box>

      {/* Schema-driven configuration form */}
      {schema && (
        <Box className={styles.form}>
          <JsonForms
            schema={schema}
            uischema={uiSchema}
            data={data}
            renderers={materialRenderers}
            cells={materialCells}
            onChange={({ data: nextData, errors }) => {
              setData(nextData as ConfigParameters);
              setFormErrors(errors ?? []);
            }}
          />
        </Box>
      )}

      {/* Footer: overwrite toggle + deploy */}
      <Box className={styles.footer}>
        <FormControlLabel
          control={
            <Switch
              checked={overwrite}
              onChange={(event) => setOverwrite(event.target.checked)}
            />
          }
          label="Overwrite full configuration"
        />
        <Box className={styles.deployControl}>
          {disabledReason && (
            <Typography variant="caption" color="text.secondary">
              {disabledReason}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleOpenDeploy}
            disabled={deployDisabled || opening}
          >
            {opening ? 'Checking…' : 'Deploy Configuration'}
          </Button>
        </Box>
      </Box>

      {deployedId && (
        <Alert severity="success" className={styles.banner}>
          Deployment accepted by Mender (id: {deployedId}).
        </Alert>
      )}

      <RecentDeployments deviceName={deviceName} refreshToken={refreshToken} />

      <ConfigDiffDialog
        open={dialogOpen}
        deviceName={deviceName}
        diff={displayDiff}
        overwrite={overwrite}
        deploying={deploying}
        onConfirm={handleDeploy}
        onCancel={() => setDialogOpen(false)}
      />
    </Box>
  );
};

export default DeviceConfiguration;
